import i18next from "i18next";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import warning from "../../../../assets/images/icons/alert-triangle.svg";
import BloodType from "../../../../assets/images/icons/BloodType.svg";
import Weight from "../../../../assets/images/icons/BodyWeight.svg";
import Height from "../../../../assets/images/icons/Height.svg";
import AlcoholDrinker from "../../../../assets/images/icons/AlcoholDrinker.svg";
import Smoker from "../../../../assets/images/icons/Smoker.svg";
import Pregnant from "../../../../assets/images/icons/Pregnant.svg";
import BMI from "../../../../assets/images/icons/BMI.svg";
import user from "../../../../assets/images/icons/user.svg";
import { ProfileAvatar } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { useAppMediaQuery, useGetData } from "../../../../hooks";
import {
  GetAppointment,
  GetPatientInfo,
} from "../../../../interface/GeneralResponse";
import {
  AppointmentPatientCard,
  TitleNavLinkOfPatientProfile,
} from "../component";
import PatientPersonalTitleDetails from "./PatientPersonalTitleDetails";

function PatientProfileSidebar() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { isMobileOrTablet } = useAppMediaQuery();

  const { data, isPending, isLoading } = useGetData<GetPatientInfo>(
    EndPoints.GetPatients,
    {},
    id,
  );
  const PatientsData = data?.Data;
  const DeletedDate = data?.Data?.DeletedAt;
  const { data: appointmentData } = useGetData<GetAppointment[]>(
    EndPoints.GetLastAppointmentForPatient,
    {},
    id,
  );

  const AppointmentData = appointmentData?.Data;

  const HealthInfo = [
    {
      name: "body_weight",
      image: Weight,
      title: PatientsData?.Weight || "____",
      unit: t("kg"),
    },
    {
      name: "height",
      image: Height,
      title: PatientsData?.Height || "____",
      unit: t("cm"),
    },
    {
      name: "blood_type",
      image: BloodType,
      title: PatientsData?.BloodType || "____",
    },
    {
      name: "alcohol_drinker",
      image: AlcoholDrinker,
      title: PatientsData?.Alcohol ? t("yes") : t("no"),
    },
    {
      name: "smoker",
      image: Smoker,
      title: PatientsData?.IsSmoker ? t("yes") : t("no"),
    },
    ...(PatientsData?.Gender !== "Male"
      ? [
          {
            name: "pregnant",
            image: Pregnant,
            title: PatientsData?.Pregnant
              ? t("week") + " " + PatientsData?.Pregnant
              : t("no"),
          },
        ]
      : []),
    {
      name: "body_mass_index",
      image: BMI,
      title: PatientsData?.BMI?.toFixed(2) || "____",
    },
  ];
 const allNavItems = [
   {
     route: isMobileOrTablet
       ? `/patients_list/${id}/medications/mobile`
       : `/patients_list/${id}/medications`,
     name: "medications_taken",
   },
   {
     name: "medical_record",
     route: isMobileOrTablet
       ? `/patients_list/${id}/medical-records/all/details`
       : `/patients_list/${id}/medical-records`,
   },
   {
     route: isMobileOrTablet
       ? `/patients_list/${id}/prescriptions_by_me/mobile`
       : `/patients_list/${id}/prescriptions_by_me`,
     name: "prescriptions_by_me",
   },
   {
     route: isMobileOrTablet
       ? `/patients_list/${id}/prescriptions_by_doctors/mobile`
       : `/patients_list/${id}/prescriptions_by_doctors`,
     name: "prescriptions_by_doctors",
   },
   {
     route: isMobileOrTablet
       ? `/patients_list/${id}/allergy/mobile`
       : `/patients_list/${id}/allergy`,
     name: "allergy",
   },
   {
     route: isMobileOrTablet
       ? `/patients_list/${id}/family_history/mobile`
       : `/patients_list/${id}/family_history`,
     name: "family_history",
   },
   {
     route: isMobileOrTablet
       ? `/patients_list/${id}/chronic_diseases/mobile`
       : `/patients_list/${id}/chronic_diseases`,
     name: "chronic_diseases",
   },
 ];

 const patientProfileNavbar = DeletedDate
   ? allNavItems.filter(
       (item) =>
         item.name === "medical_record" || item.name === "prescriptions_by_me",
     )
   : allNavItems;
   
  moment.locale(i18next.language);
  return (
    <div className="box flex w-full min-w-96 flex-col gap-2 overflow-hidden !p-4 lg:h-[80dvh] lg:w-1/3">
      {isLoading ? (
        <div className="flex animate-pulse items-center gap-2">
          <div className="h-14 w-14 rounded-full bg-body-text" />
          <div className="flex flex-col gap-3">
            <div className="h-4 w-32 rounded-sm bg-body-text" />
            <div className="h-4 w-48 rounded-sm bg-body-text" />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <ProfileAvatar
            imageToLoad={user}
            isLoad={isPending}
            Id={PatientsData?.Id}
            src={PatientsData?.Image}
            alt={PatientsData?.FullName}
            FullName={PatientsData?.FullName}
            withPreview
            className={`h-16 w-16 rounded-full border ${PatientsData?.Image ? "!object-cover" : ""}`}
          />
          <PatientPersonalTitleDetails PatientsData={PatientsData} />
        </div>
      )}
      <div className="flex gap-2 rounded-lg bg-orange-00 px-2 py-1.5 text-sm text-orange">
        <img src={warning} alt="warning" className="h-5 w-5" />
        <p>
          {t(
            "you_can_only_change_Patient_health_information_in_the_medical_record",
          )}
        </p>
      </div>
      <div className="overflow-y-auto">
        {!DeletedDate && (
          <div className="grid grid-cols-3 gap-2">
            {HealthInfo?.map((e, index) => (
              <div
                key={index}
                className={`flex-1 rounded-lg bg-InputFieldColor p-2 ${
                  index === HealthInfo.length - 1 && HealthInfo.length % 3 !== 0
                    ? "col-span-3"
                    : ""
                }`}
              >
                <p className="mb-2 text-sm">{t(e.name)}</p>
                <div className="flex items-center gap-2">
                  <img src={e.image} alt={e.name} className="h-8 w-8" />
                  <div className="flex items-end gap-0.5">
                    <p className="font-bold">{e.title}</p>
                    <p className="text-xs text-body-text">{e.unit}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <hr className="my-5" />
        {AppointmentData && (
          <>
            <div className="text-primary-textFont mb-4 text-sm font-semibold">
              {t("last_appointment")}
            </div>
            <AppointmentPatientCard item={AppointmentData} />
          </>
        )}
        <div className="mt-4 flex flex-col gap-1">
          {patientProfileNavbar?.map((e, i) => (
            <TitleNavLinkOfPatientProfile key={i} label={e.name} to={e.route} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default PatientProfileSidebar;
